import { useEffect, useState } from 'react'
import { Outlet, Navigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAuthStore } from '@/store/auth'
import { useTenantStore } from '@/store/tenant'
import { useUIStore } from '@/store/ui'
import { getSettings } from '@/api/settings'
import { Sidebar } from './Sidebar'
import { Header } from './Header'
import { cn } from '@/utils/cn'

/**
 * Root layout for all authenticated pages.
 *
 * On mount it:
 * 1. Guards the route — redirects to /login if not authenticated.
 * 2. Fetches /users/me and /user-types/{id}/permissions (via loadUser/loadPermissions).
 * 3. Fetches GET /settings and applies tenant branding (via applySettingsResponse).
 *
 * A full-screen spinner is shown while (2) is in-flight so the sidebar never
 * renders with an empty permission map.
 */
export function AppLayout() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const user = useAuthStore((s) => s.user)
  const permissionsLoaded = useAuthStore((s) => s.permissionsLoaded)
  const loadUser = useAuthStore((s) => s.loadUser)
  const loadPermissions = useAuthStore((s) => s.loadPermissions)
  const applySettingsResponse = useTenantStore((s) => s.applySettingsResponse)
  const sidebarCollapsed = useUIStore((s) => s.sidebarCollapsed)

  const [bootstrapping, setBootstrapping] = useState(!permissionsLoaded)

  // All hooks must be called unconditionally before any early returns.
  // Bootstrap user + permissions on first mount (page refresh / first load).
  useEffect(() => {
    if (isAuthenticated && !permissionsLoaded) {
      setBootstrapping(true)
      const run = user
        ? loadPermissions()
        : loadUser().then(() => loadPermissions())
      run
        .catch(console.error)
        .finally(() => setBootstrapping(false))
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Fetch domain settings for tenant branding
  useQuery({
    queryKey: ['settings'],
    queryFn: getSettings,
    staleTime: 10 * 60_000,
    enabled: isAuthenticated,
    select: (data) => {
      applySettingsResponse(data as Record<string, unknown>)
      return data
    },
  })


  // Guard — redirect to login after all hooks have been called
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  if (bootstrapping) {
    return (
      <div className="flex h-screen items-center justify-center bg-surface-raised">
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-sm text-muted">Loading…</p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-screen overflow-hidden bg-surface-raised">
      <Sidebar />
      <div
        className={cn(
          'flex flex-1 flex-col overflow-hidden transition-all duration-200',
          sidebarCollapsed ? 'ms-16' : 'ms-64'
        )}
      >
        <Header />
        <main className="flex-1 overflow-y-auto bg-surface-raised p-6">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
